import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';

const EMPTY = {
  idVehiculo: 0,
  idReserva: 0,
  fechaLectura: '',
  kmOdometro: '',
  origen: '',
  notas: '',
};

const endpointVehiculo = ENDPOINTS.vehiculo;
const modeloVehiculo = Models.vehiculo;

// Helper para armar etiqueta “bonita” del vehículo
function buildVehiculoLabel(v) {
  if (!v) return '';
  const placa = v.placa || 'S/placa';
  const marca = v.marca || '';
  const modelo = v.modelo || '';
  const anio = v.anio || '';
  const detalle = [marca, modelo, anio].filter(Boolean).join(' ');
  return detalle ? `${placa} · ${detalle}` : placa;
}

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idVehiculo: '',
    idReserva: '',
    fechaLectura: '',
    kmOdometro: '',
    origen: '',
    notas: '',
  });
  const [vehiculos, setVehiculos] = useState([]);

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idVehiculo':
        if (!value || Number(value) <= 0) error = 'Selecciona un vehículo.';
        break;
      case 'idReserva':
        if (!value || Number(value) <= 0) error = 'El ID de reserva es obligatorio.';
        break;
      case 'fechaLectura':
        if (!value) error = 'La fecha de lectura es obligatoria.';
        break;
      case 'kmOdometro':
        if (value === '' || value === null || value === undefined) error = 'El kilometraje es obligatorio.';
        break;
      case 'origen':
        if (!value) error = 'El origen es obligatorio.';
        break;
      case 'notas':
        if (!value) error = 'Las notas son obligatorias.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['idVehiculo', 'idReserva', 'fechaLectura', 'kmOdometro', 'origen', 'notas'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  // Cargar vehículos para el select
  useEffect(() => {
    const loadVehiculos = async () => {
      try {
        const res = await axios.get(endpointVehiculo);
        setVehiculos((res.data || []).map((x) => modeloVehiculo(x)));
      } catch (e) {
        console.error('Error al cargar vehículos para Odómetro', e);
      }
    };
    loadVehiculos();
  }, []);

  // Cargar datos al editar
  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        idLectura: data.idLectura ?? 0,
        idVehiculo: data.idVehiculo ?? 0,
        idReserva: data.idReserva ?? 0,
        fechaLectura: (data.fechaLectura ?? '').slice(0, 10),
        kmOdometro: data.kmOdometro ?? '',
        origen: data.origen ?? '',
        notas: data.notas ?? '',
      };
      setForm(filled);
      validateField('idVehiculo', filled.idVehiculo);
      validateField('idReserva', filled.idReserva);
      validateField('fechaLectura', filled.fechaLectura);
      validateField('kmOdometro', filled.kmOdometro);
      validateField('origen', filled.origen);
      validateField('notas', filled.notas);
    } else {
      setForm(EMPTY);
      setErrors({ idVehiculo: '', idReserva: '', fechaLectura: '', kmOdometro: '', origen: '', notas: '' });
    }
  }, [mode, data]);

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled =
    !form.idVehiculo ||
    !form.idReserva ||
    !form.fechaLectura ||
    form.kmOdometro === '' ||
    !form.origen?.trim() ||
    !form.notas?.trim() ||
    hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva lectura de odómetro' : 'Editar lectura de odómetro'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Vehículo */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Vehículo:</label>
        <br /><br />
        <select
          className={`infoField${errors.idVehiculo ? ' infoField--error' : ''}`}
          value={form.idVehiculo}
          onChange={(e) => handleChange('idVehiculo', Number(e.target.value) || 0)}
        >
          <option value={0}>-- Selecciona un vehículo --</option>
          {vehiculos.map((v) => (
            <option key={v.idVehiculo} value={v.idVehiculo}>
              {buildVehiculoLabel(v)}
            </option>
          ))}
        </select>
        {errors.idVehiculo && <small style={errorStyle}>{errors.idVehiculo}</small>}
      </div>

      {/* Id Reserva (libre por ahora) */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Id Reserva:</label>
        <br /><br />
        <input
          className={`infoField${errors.idReserva ? ' infoField--error' : ''}`}
          type="number"
          min={1}
          value={form.idReserva}
          onChange={(e) => handleChange('idReserva', Number(e.target.value) || 0)}
        />
        {errors.idReserva && <small style={errorStyle}>{errors.idReserva}</small>}
      </div>

      {/* Fecha */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha de lectura:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaLectura ? ' infoField--error' : ''}`}
          type="date"
          onKeyDown={(e) => e.preventDefault()}
          value={form.fechaLectura}
          onChange={(e) => handleChange('fechaLectura', e.target.value)}
          onBlur={(e) => validateField('fechaLectura', e.target.value)}
        />
        {errors.fechaLectura && <small style={errorStyle}>{errors.fechaLectura}</small>}
      </div>

      {/* KM */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>KM Odómetro:</label>
        <br /><br />
        <input
          className={`infoField${errors.kmOdometro ? ' infoField--error' : ''}`}
          type="number"
          step="any"
          value={form.kmOdometro}
          onChange={(e) => handleChange('kmOdometro', e.target.value)}
        />
        {errors.kmOdometro && <small style={errorStyle}>{errors.kmOdometro}</small>}
      </div>

      {/* Origen */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Origen:</label>
        <br /><br />
        <input
          className={`infoField${errors.origen ? ' infoField--error' : ''}`}
          value={form.origen}
          onChange={(e) => handleChange('origen', e.target.value)}
          maxLength={120}
        />
        {errors.origen && <small style={errorStyle}>{errors.origen}</small>}
      </div>

      {/* Notas */}
      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Notas:</label>
        <br /><br />
        <textarea
          className={`infoField${errors.notas ? ' infoField--error' : ''}`}
          value={form.notas}
          onChange={(e) => handleChange('notas', e.target.value)}
          rows={3}
          maxLength={500}
        />
        {errors.notas && <small style={errorStyle}>{errors.notas}</small>}
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) onSubmit(form, mode);
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
